import { BymeService } from './byme.service';
import { Injectable, Injector } from "@angular/core";
import { environment } from '../../environments/environment';
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { UserService } from './user.service';
import { MODAL, ModalService } from './modal.service';
import { TranslateService } from '@ngx-translate/core';
import { CONTEXT } from '../enum/context';
import { MAGENTO_MESSAGES } from '../enum/magento-messages';
import { GTM } from './gtm.service';
import { CurrentService } from './current.service';

@Injectable({
    providedIn: 'root'
})
export class MagentoService {

    // State du isLoading
    private loadingSource = new BehaviorSubject(true)
    loadingState = this.loadingSource.asObservable();
    // Magento Infos
    hbmUserId: string;
    magentoUserId: string;
    completeName: string;
    firstName: string;
    lastName: string;
    storeId: string;
    role: string;
    country: string;
    homepage: boolean = true;
    context: string;
    projectsModalToOpen: boolean = false;
    init: boolean = false;
    timeoutID: any;
    origin: string;

    constructor(private injector: Injector,
        private userService: UserService,
        private modalService: ModalService,
        private translateService: TranslateService,
        private currentService: CurrentService,
        private router: Router,
        private gtmService: GTM
    ) { }

    handleMagentoMessage = (e) => {
        //le message ne vient pas de l'application appelante
        if (!document.referrer.startsWith(e.origin)) {
            return;
        }

        //le message ne vient pas d'une application autorisée
        if (!environment.magento.whiteListUrls.some(u => e.origin.startsWith(u))) {
            this.router.navigate(['/error']);
            return;
        }

        //le message n'a pas de data
        if (!e.data) {
            this.router.navigate(['/error']);
            return;
        }

        if (!environment.production) {
            console.log("%cwindow.handleMagentoMessage()", "background-color:green;color:white", "magento -> simphonis", e)
        }

        this.origin = e.origin;

        const byMeService = this.injector.get(BymeService);

        switch (e.data.event) {
            case MAGENTO_MESSAGES.CREATE_ACCOUNT: {
                byMeService.createUser(false).then(hbmUserId => {
                    this.hbmUserId = hbmUserId;
                    //ensuite on récupère un nouveau token et on se connecte
                    byMeService.acquireToken(hbmUserId, true).then(() => {
                        byMeService.loginForSaveRequested = true;      //on attend un loginSucceeded pour la suite
                    })
                })
                break;
            }
            case MAGENTO_MESSAGES.INIT: {
                this.init = true;
                clearInterval(this.timeoutID);

                // lecture des données utilisateur
                this.handleUserData(e.data.content);
                
                // traduction + produits en fonction du pays
                const c = e.data.content['country'];
                this.country = c ? c.toLowerCase() : environment.countries[0];
                if (environment.countries.includes(this.country)) {
                    this.translateService.setDefaultLang(this.country);
                }
                
                //context
                this.context = e.data.content['context'];
                this.userService.context = this.context;
                this.userService.initContext = this.context;
                // accès direct ou page d'accueil                        
                if (e.data.content['homepage']) {
                    if (e.data.content['homepage'] === 'true') {
                        this.homepage = true;
                    } else if (e.data.content['homepage'] === 'false') {
                        this.homepage = false;
                    }
                }

                //project
                if (e.data.content['project_id']) {
                    this.currentService.project = { uuid: e.data.content['project_id'] };
                }
                //on met à jour les données utilisateur si on a changé de compte ou si on n'était pas connecté
                if (!this.userService.userDatas || this.userService.userDatas?.magentoId != this.magentoUserId) {
                    this.updateUserData();
                }
                
                this.postInit();
                break;
            }
            case MAGENTO_MESSAGES.LOGIN_SUCCESS: {
                this.handleUserData(e.data.content);
                // if (!this.userService.userDatas || this.userService.userDatas?.vendor || this.userService.)
                this.updateUserData();
                
                // Si l'utilisateur Magento n'a pas de HBM user id -> on créer le compte.
                if (this.hbmUserId == null && !this.projectsModalToOpen) {
                    byMeService.createUser(false).then(hbmUserId => {
                        this.hbmUserId = hbmUserId;
                        //ensuite on récupère un nouveau token et on se connecte
                        byMeService.acquireToken(hbmUserId, true).then(() => {
                            // On envoie le hbm user id à Magento.
                            this.postMessageToMagento(
                                MAGENTO_MESSAGES.MATCH_ACCOUNT,
                                { 
                                    hbm_user_id: this.hbmUserId, 
                                    magento_id: this.userService.userDatas.magentoId,
                                    role: this.userService.userDatas.vendor ? 'vendor' : 'customer'
                                });
                        })
                    })
                } else { //sinon on se connecte
                    byMeService.acquireToken(this.hbmUserId, true);
                    if (this.projectsModalToOpen) {
                        this.projectsModalToOpen = false;
                        this.modalService.display(MODAL.PROJECTS, { customerId: this.hbmUserId });
                    }
                }
                break;
            }
            default:
                return;
        }
    }

    handleUserData(content) {
        this.hbmUserId = content['hbm_user_id'];
        this.magentoUserId = content['magento_user_id'];
        this.lastName = content['lastname']?.trim();
        this.firstName = content['firstname']?.trim();
        this.completeName = `${content['lastname'] ? content['lastname'] : ""} ${content['firstname'] ? content['firstname'] : ""}`.trim();
        this.storeId = content['store_id'];
        this.role = content['role'];
        if (content['cookie_consent']) {
            this.gtmService.activate();
        }
    }

    updateUserData() {
        this.userService.userDatas = {
            hbmId: this.hbmUserId,
            magentoId: this.magentoUserId,
            completeName: this.completeName,
            firstName: this.firstName,
            lastName: this.lastName,
            storeId: this.storeId,
            vendor: this.role === "vendor",
            country: this.country
        };
    }

    postMessageToMagento(event, data = null) {
        if (window.parent) {
            if (!environment.production) {
                console.log("%cplanner.postMessageToIFrame()", "background-color:blue;color:white", "simphonis -> magento", event, data);
            }            
            window.parent.postMessage({
                event: event,
                content: data
            }, document.referrer);
        }
    }

    private changeLoadingState(state: boolean) {
        this.loadingSource.next(state);
    }

    private postInit() {
        // on retire le loading de la page principale
        this.changeLoadingState(false);

        let route = '/';

        if (!this.homepage || this.currentService.project) {
            if (this.context === CONTEXT.KITCHEN && !this.currentService.project) {
                route = '/kitchen/design'; // need mapping
            } else {
                route = `/${this.context}/configuration`; // HBM
            }
        }

        this.router.navigate([route]);
    }
}